// React and MUI imports
import { colors as themeColors } from '../utils/theme';
import { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography, TextField, MenuItem, IconButton, Divider, Button } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Custom context for managing dispatch cart (add/remove/update)
import { useDispatchCart, getVariantKey } from '../context/DispatchContext';

// Routing hooks for reading route or location-based client ID
import { useLocation, useParams } from 'react-router-dom';

// Backend API URL
const API_URL = `http://192.168.1.248:3103/api`;

// Main DispatchCart Component
export default function DispatchCart({ loggedInUser, companyData }) {
    // Extract clientId from route param (e.g., /dispatch-cart/:clientId)
    const { clientId: paramClientId } = useParams();

    // Get clientId passed via location state (optional alternative)
    const location = useLocation();
    const routeClientId = location.state?.clientId;

    // Resolve final clientId to filter items (priority: param > route > null)
    const clientId = paramClientId || routeClientId || null;

    // Get cart, dispatch update and remove handlers from context
    const { cart, updateDispatchQty, removeItem } = useDispatchCart();
    const navigate = useNavigate();

    // Options for dropdowns (fetched from master tables)
    const [colorOptions, setColorOptions] = useState([]);
    const [sizeOptions, setSizeOptions] = useState([]);

    // Convert cart object into an array of items
    const allItems = Object.values(cart);

    // Filter items belonging only to this client if a clientId is provided
    const items = clientId ? allItems.filter(i => i.clientId === clientId) : allItems;

    // Debug logs (optional — can be removed in production)
    console.log('🔍 clientId filter active:', clientId);
    console.log('🛒 Total cart items:', allItems.length);
    console.log('✅ Filtered dispatch items:', items);

    // Fetch size and color master data on initial mount
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [colorRes, sizeRes] = await Promise.all([
                    axios.get(`${API_URL}/colormaster`),
                    axios.get(`${API_URL}/sizemaster`)
                ]);
                setColorOptions(colorRes.data || []);
                setSizeOptions(sizeRes.data || []);
            } catch (err) {
                console.error('Error fetching color/size options:', err);
            }
        };
        fetchOptions();
    }, []);

    const { removeItemsByVariantKeys } = useDispatchCart(); // ✅ use it from context

    // Handles click on 'Proceed to Dispatch'
    const handleDispatch = async () => {
        try {
            // Step 1: Build items array
            const dispatchItems = items.map(product => {
                const qty = +product.selectedQty || 1;
                const rate = +product.saleRate || 0;
                const gstPct = +product.taxName || +product.igst || 0;
                const exclTotal = rate * qty;
                const gstAmt = (exclTotal * gstPct) / 100;
                const inclTotal = exclTotal + gstAmt;

                return {
                    productId: product.productId,
                    SODtsID: product.SODtsID,
                    SOAutoID: product.SOAutoID,
                    itemCode: product.itemCode,
                    description: product.description,
                    ItemSize: product.selectedSize,
                    ItemColor: product.selectedColor,
                    Unit: product.unit,
                    Qty: product.dispatchQty,
                    Rate: rate,
                    Per: product.unit,
                    HSN: product.HSN,
                    IGST: gstPct,
                    IGSTAmt: gstAmt,
                    exclTotal,
                    inclTotal,
                    clientId: product.clientId,
                    clientname: product.clientname // ✅ required for variantKey
                };
            });

            // Step 2: Aggregate totals
            const BasicAmt = dispatchItems.reduce((sum, item) => sum + item.exclTotal, 0);
            const IGSTAmt = dispatchItems.reduce((sum, item) => sum + item.IGSTAmt, 0);
            const BillAmt = dispatchItems.reduce((sum, item) => sum + item.inclTotal, 0);

            // Step 3: Create master payload
            const groupedChallan = {
                PartyID: clientId,
                ChallanDate: new Date().toISOString(),
                BasicAmt: +BasicAmt.toFixed(2),
                IGSTAmt: +IGSTAmt.toFixed(2),
                BillAmt: +BillAmt.toFixed(2),
                CGSTAmt: 0,
                SGSTAmt: 0,
                items: dispatchItems
            };

            const payload = {
                challans: [groupedChallan]
            };

            console.log('🚚 Final Dispatch Payload:', payload);

            // Step 4: POST to backend
            const response = await axios.post(`${API_URL}/sale-challan`, payload);
            console.log('✅ Dispatch Successful:', response.data);

            // ✅ Step 5: Remove dispatched items by their variant keys
            const dispatchedKeys = dispatchItems.map(item => getVariantKey(item));
            removeItemsByVariantKeys(dispatchedKeys);

            alert('Dispatch created successfully!');
        } catch (error) {
            console.error('❌ Dispatch Error:', error.response?.data || error.message);
            alert('Failed to dispatch. Please try again.');
        }
    };

    return (
        <Box p={2} maxWidth="1100px" mx="auto">
            {/* Header with back button and title */}
            <Box display="flex" alignItems="center" mb={2}>
                <Button onClick={() => navigate(-1)}>← Back</Button>
                <Typography variant="h5" fontWeight={600} sx={{ ml: 2 }}>📦 Dispatch Cart</Typography>
            </Box>

            {/* Empty state if no items in the filtered dispatch cart */}
            {items.length === 0 ? (
                <Typography>No items in dispatch cart.</Typography>
            ) : (
                // Render each dispatch item card
                items.map((product, idx) => {
                    // Prepare values for UI and calculations
                    const rate = +product.saleRate || 0;
                    const gst = +product.taxName || product.igst || 0;
                    const qty = +product.selectedQty || 1;
                    const maxLevel = +product.maxLevel || 0;
                    const exclTotal = rate * qty;
                    const gstAmt = (exclTotal * gst) / 100;
                    const inclTotal = exclTotal + gstAmt;
                    const borderColor = qty <= maxLevel ? '#25D366' : '#FF2800'; // green if valid, red if invalid

                    return (
                        <Paper key={product.variantKey} elevation={2}
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 2,
                                bgcolor: themeColors.neutral.surfaceMuted,
                                borderLeft: `4px solid ${borderColor}`
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
                                <Box flexGrow={1} minWidth="280px">
                                    {/* Product info */}
                                    <Typography variant="subtitle2" fontWeight={600}>{product.itemCode}</Typography>
                                    <Typography variant="body2" color="text.secondary" mb={0.5}>{product.description}</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                        Unit: {product.unit} | HSN: {product.HSN}
                                    </Typography>

                                    {/* Size, color, and dispatch qty inputs (disabled except dispatch qty) */}
                                    <Box display="flex" gap={1} flexWrap="wrap" mb={1} alignItems="center">
                                        {/* Size dropdown (read-only) */}
                                        <TextField label="Size" select size="small" value={product.selectedSize} disabled sx={{ minWidth: 120 }}>
                                            {sizeOptions.map(o => (
                                                <MenuItem key={o.sizeId} value={o.sizeId}>{o.itemSize}</MenuItem>
                                            ))}
                                        </TextField>

                                        {/* Color dropdown (read-only) */}
                                        <TextField label="Color" select size="small" value={product.selectedColor} disabled sx={{ minWidth: 140 }}>
                                            {colorOptions.map(o => (
                                                <MenuItem key={o.colorId} value={o.colorId}>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: o.colorCode, border: `1px solid ${themeColors.neutral.lineStrong}` }} />
                                                        {o.itemColor}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        {/* Quantity & Limits */}
                                        <TextField label="Qty" size="small" value={qty} disabled sx={{ width: 60 }} />
                                        <TextField label="Max Level" size="small" value={product.maxLevel || ''} disabled sx={{ width: 80 }} />

                                        {/* Dispatch Quantity Input (editable) */}
                                        <TextField
                                            label="Dispatch"
                                            type="number"
                                            size="small"
                                            value={product.dispatchQty}
                                            inputProps={{ min: 0, max: Math.min(qty, maxLevel) }}
                                            sx={{ width: 80 }}
                                            onChange={(e) => {
                                                const val = +e.target.value;
                                                const limit = Math.min(qty, maxLevel);
                                                if (val >= 0 && Number.isInteger(val) && val <= limit) {
                                                    updateDispatchQty(product.variantKey, val);
                                                }
                                            }}
                                            onBlur={() => {
                                                const limit = Math.min(qty, maxLevel);
                                                if (!Number.isInteger(product.dispatchQty) || product.dispatchQty < 0 || product.dispatchQty > limit) {
                                                    updateDispatchQty(product.variantKey, limit);
                                                }
                                            }}
                                        />
                                    </Box>

                                    {/* Price Summary Grid */}
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">₹{rate}×{qty}</Typography>
                                            <Typography variant="body2">= ₹{exclTotal}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">GST ({gst}%)</Typography>
                                            <Typography variant="body2">₹{gstAmt.toFixed(2)}</Typography>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Right Section: Total and Delete */}
                                <Box minWidth="160px" textAlign="right" display="flex" flexDirection="column" justifyContent="space-between">
                                    <IconButton color="error" onClick={() => removeItem(product.variantKey)}><Delete /></IconButton>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Total</Typography>
                                        <Typography variant="subtitle1" fontWeight={700} color="primary">₹{inclTotal.toFixed(2)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    );
                })
            )}

            {/* Final Action Button */}
            {items.length > 0 && (
                <Box textAlign="right" mt={4}>
                    <Button variant="contained" color="primary" size="large" onClick={handleDispatch}>
                        Proceed to Dispatch
                    </Button>
                </Box>
            )}
        </Box>
    );
}   